import { hasNativeCapability, isRunningInBiltNativeShell, saveNativeImage } from './nativeShell';

export type ImageSaveDestination = 'photos' | 'share_sheet' | 'browser';

function webpToJpeg(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('This archived photo could not be opened. Try saving it from mobile web.'));
    };
    image.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('This archived photo could not be prepared for Photos.'));
        return;
      }
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
      canvas.toBlob(
        (jpeg) => jpeg ? resolve(jpeg) : reject(new Error('This archived photo could not be prepared for Photos.')),
        'image/jpeg',
        0.92
      );
    };
    image.src = url;
  });
}

/** WKWebView ignores browser download links; the native shell saves supported images to Photos. */
export async function saveImage(blob: Blob, filename: string): Promise<ImageSaveDestination> {
  const extension = blob.type === 'image/png' ? '.png' : blob.type === 'image/jpeg' ? '.jpg' : blob.type === 'image/webp' ? '.webp' : blob.type === 'image/svg+xml' ? '.svg' : null;
  if (!extension) throw new Error('This image must be a PNG, JPEG, WebP, or SVG to save.');
  if (isRunningInBiltNativeShell()) {
    if (!hasNativeCapability('native_image_save') ||
      (extension === '.svg' && !hasNativeCapability('native_image_svg')) ||
      ((extension === '.jpg' || extension === '.webp') && !hasNativeCapability('native_image_jpeg'))) {
      throw new Error('Update the Bilt:Spec app to save this image to Photos.');
    }
    const saveBlob = extension === '.webp' ? await webpToJpeg(blob) : blob;
    const saveExtension = extension === '.webp' ? '.jpg' : extension;
    const safeName = (filename.replace(/\.(?:png|jpe?g|webp|svg)$/i, '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^[^a-zA-Z0-9]+/, '')
      .slice(0, 115) || 'bilt-image') + saveExtension;
    return saveNativeImage(saveBlob, safeName);
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.replace(/\.(?:png|jpe?g|webp|svg)$/i, '') + extension;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  return 'browser';
}

export function imageSaveMessage(destination: ImageSaveDestination): string {
  return destination === 'photos' ? 'Saved to Photos.' :
    destination === 'share_sheet' ? 'Share sheet closed. Check Photos or Files if you chose a save action.' :
    'Image download started.';
}
